import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, firestore } from '../../firebase/config';
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  doc, 
  getDoc, 
  updateDoc, 
  serverTimestamp,
  orderBy
} from 'firebase/firestore';
import LocationPicker from './LocationPicker';

// JobModal component extracted outside to prevent re-creation and input focus loss
const JobModal = ({ 
  show, 
  onClose, 
  isEdit = false,
  jobForm,
  setJobForm,
  skillInput,
  setSkillInput,
  benefitInput,
  setBenefitInput,
  useBusinessLocation,
  setUseBusinessLocation,
  customLocation,
  setCustomLocation,
  setShowLocationPicker,
  businessProfile,
  memoizedBusinessAddress,
  submitting,
  onSubmit,
  onAddSkill,
  onRemoveSkill,
  onAddBenefit,
  onRemoveBenefit
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg w-full max-h-[95vh] overflow-y-auto ${
        !useBusinessLocation ? 'max-w-4xl' : 'max-w-2xl'
      }`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              {isEdit ? 'Edit Job Post' : 'Create New Job Post'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            {/* Job Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Title *
              </label>
              <input
                type="text"
                required
                value={jobForm.title}
                onChange={(e) => setJobForm({...jobForm, title: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                placeholder="e.g., Server, Chef, Manager"
              />
            </div>

            {/* Job Type and Experience Level */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Type *
                </label>
                <select
                  required
                  value={jobForm.jobType}
                  onChange={(e) => setJobForm({...jobForm, jobType: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Select Type</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Temporary">Temporary</option>
                  <option value="Seasonal">Seasonal</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Experience Level *
                </label>
                <select
                  required
                  value={jobForm.experienceLevel}
                  onChange={(e) => setJobForm({...jobForm, experienceLevel: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Select Level</option>
                  <option value="Entry Level">Entry Level</option>
                  <option value="1-2 years">1-2 years</option>
                  <option value="3-5 years">3-5 years</option>
                  <option value="5+ years">5+ years</option>
                  <option value="Manager">Manager</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Description *
              </label>
              <textarea
                required
                rows={4}
                value={jobForm.description}
                onChange={(e) => setJobForm({...jobForm, description: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                placeholder="Describe the role, responsibilities, and what you're looking for..."
              />
            </div>

            {/* Work Schedule */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Work Schedule
              </label>
              <input
                type="text"
                value={jobForm.workSchedule}
                onChange={(e) => setJobForm({...jobForm, workSchedule: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                placeholder="e.g., Monday-Friday 9am-5pm, Weekends, Flexible hours"
              />
            </div>

            {/* Start Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time
              </label>
              <input
                type="text"
                value={jobForm.startTime}
                onChange={(e) => setJobForm({...jobForm, startTime: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                placeholder="e.g., Immediately, Next Monday, 2 weeks notice"
              />
            </div>

            {/* Pay Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hourly Rate ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={jobForm.hourlyRate}
                  onChange={(e) => setJobForm({...jobForm, hourlyRate: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g., 15.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Salary (if different)
                </label>
                <input
                  type="text"
                  value={jobForm.salary}
                  onChange={(e) => setJobForm({...jobForm, salary: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g., $35,000/year or $15-18/hr"
                />
              </div>
            </div>

            {/* Required Skills */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Required Skills
              </label>
              <div className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), onAddSkill())}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter a skill and press Enter"
                />
                <button
                  type="button"
                  onClick={onAddSkill}
                  className="px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {jobForm.requiredSkills.map((skill, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
                  >
                    {skill}
                    <button
                      type="button"
                      onClick={() => onRemoveSkill(skill)}
                      className="ml-1 text-orange-600 hover:text-orange-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Extra Compensations */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Extra Compensations
              </label>
              <div className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={benefitInput}
                  onChange={(e) => setBenefitInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), onAddBenefit())}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter a benefit and press Enter"
                />
                <button
                  type="button"
                  onClick={onAddBenefit}
                  className="px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {jobForm.extraCompensations.map((benefit, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
                  >
                    {benefit}
                    <button
                      type="button"
                      onClick={() => onRemoveBenefit(benefit)}
                      className="ml-1 text-orange-600 hover:text-orange-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Job Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Job Location *
              </label>
              
              {/* Location Options */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="business-location"
                    name="location-type"
                    checked={useBusinessLocation}
                    onChange={() => {
                      setUseBusinessLocation(true);
                      setCustomLocation(null);
                      setShowLocationPicker(false);
                    }}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                  />
                  <label htmlFor="business-location" className="ml-2 block text-sm text-gray-900">
                    Use Business Location
                    {businessProfile?.address && (
                      <span className="text-gray-500 ml-1">
                        ({businessProfile.address.city}, {businessProfile.address.state})
                      </span>
                    )}
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="custom-location"
                    name="location-type"
                    checked={!useBusinessLocation}
                    onChange={() => {
                      setUseBusinessLocation(false);
                      setShowLocationPicker(true);
                    }}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                  />
                  <label htmlFor="custom-location" className="ml-2 block text-sm text-gray-900">
                    Select Different Location
                  </label>
                </div>
              </div>

              {/* Custom Location Picker */}
              {!useBusinessLocation && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <LocationPicker
                    onLocationSelect={(location) => setCustomLocation(location)}
                    initialLocation={customLocation?.coordinates ? {
                      lat: customLocation.coordinates.latitude,
                      lng: customLocation.coordinates.longitude
                    } : null}
                    businessAddress={memoizedBusinessAddress}
                  />
                </div>
              )}

              {/* Current Location Display */}
              {useBusinessLocation && businessProfile?.address && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900 mb-1">Business Location:</p>
                  <p className="text-sm text-gray-600">
                    {businessProfile.address.street && `${businessProfile.address.street}, `}
                    {businessProfile.address.city}, {businessProfile.address.state} {businessProfile.address.zipcode}
                  </p>
                </div>
              )}

              {!useBusinessLocation && customLocation && (
                <div className="bg-slate-50 rounded-lg p-3 mt-2">
                  <p className="text-sm font-medium text-gray-900 mb-1">Selected Location:</p>
                  <p className="text-sm text-gray-600">
                    {customLocation.street && `${customLocation.street}, `}
                    {customLocation.city}, {customLocation.state} {customLocation.zipcode}
                  </p>
                  {customLocation.coordinates && (
                    <p className="text-xs text-gray-500 mt-1">
                      Coordinates: {customLocation.coordinates.latitude.toFixed(6)}, {customLocation.coordinates.longitude.toFixed(6)}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Application URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Application URL (Optional)
              </label>
              <input
                type="url"
                value={jobForm.applicationUrl}
                onChange={(e) => setJobForm({...jobForm, applicationUrl: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                placeholder="https://example.com/apply"
              />
            </div>

            {/* Immediate Start */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="immediateStart"
                checked={jobForm.isImmediateStart}
                onChange={(e) => setJobForm({...jobForm, isImmediateStart: e.target.checked})}
                className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
              />
              <label htmlFor="immediateStart" className="ml-2 block text-sm text-gray-900">
                Immediate start available
              </label>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
              >
                {submitting ? 'Saving...' : (isEdit ? 'Update Job' : 'Post Job')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const BusinessJobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [filter, setFilter] = useState('all');
  const [submitting, setSubmitting] = useState(false);

  // Job form state
  const [jobForm, setJobForm] = useState({
    title: '',
    description: '',
    jobType: '',
    experienceLevel: '',
    hourlyRate: '',
    salary: '',
    workSchedule: '',
    requiredSkills: [],
    extraCompensations: [],
    startTime: '',
    isImmediateStart: false,
    applicationUrl: ''
  });

  // New state for input fields
  const [skillInput, setSkillInput] = useState('');
  const [benefitInput, setBenefitInput] = useState('');
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [useBusinessLocation, setUseBusinessLocation] = useState(true);
  const [customLocation, setCustomLocation] = useState(null);

  // Memoize business address to prevent unnecessary LocationPicker re-renders
  const memoizedBusinessAddress = useMemo(() => {
    return businessProfile?.address || null;
  }, [businessProfile?.address]);

  useEffect(() => {
    fetchJobs();
    checkBusinessProfile();
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const userDoc = await getDoc(doc(firestore, 'users', user.uid));
      const businessId = userDoc.data().businessId;

      const q = query(
        collection(firestore, 'jobs'),
        where('businessId', '==', businessId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const jobsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setJobs(jobsData);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkBusinessProfile = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const userDoc = await getDoc(doc(firestore, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.businessId) {
          const businessDoc = await getDoc(doc(firestore, 'businesses', userData.businessId));
          if (businessDoc.exists()) {
            setBusinessProfile(businessDoc.data());
          }
        }
      }
    } catch (error) {
      console.error('Error checking business profile:', error);
    }
  };

  const handleCreateJob = () => {
    if (!businessProfile || !businessProfile.name) {
      alert('Please complete your business profile before posting jobs.');
      return;
    }
    resetJobForm();
    setShowCreateModal(true);
  };

  const handleEditJob = (job) => {
    setSelectedJob(job);
    setJobForm({
      title: job.title || '',
      description: job.description || '',
      jobType: job.jobType || '',
      experienceLevel: job.experienceLevel || '',
      hourlyRate: job.hourlyRate || '',
      salary: job.salary || '',
      workSchedule: job.workSchedule || '',
      requiredSkills: job.requiredSkills || [],
      extraCompensations: job.extraCompensations || [],
      startTime: job.startTime || '',
      isImmediateStart: job.isImmediateStart || false,
      applicationUrl: job.applicationUrl || ''
    });
    setShowEditModal(true);
  };

  const resetJobForm = useCallback(() => {
    setJobForm({
      title: '',
      description: '',
      jobType: '',
      experienceLevel: '',
      hourlyRate: '',
      salary: '',
      workSchedule: '',
      startTime: '',
      requiredSkills: [],
      extraCompensations: [],
      applicationUrl: '',
      isImmediateStart: false
    });
    setSkillInput('');
    setBenefitInput('');
    setUseBusinessLocation(true);
    setCustomLocation(null);
    setShowLocationPicker(false);
    setSelectedJob(null);
  }, []);

  const handleSubmitJob = useCallback(async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        alert('You must be logged in to post a job.');
        setSubmitting(false);
        return;
      }

      // Validate location selection
      if (!useBusinessLocation && !customLocation) {
        alert('Please select a job location before submitting.');
        setSubmitting(false);
        return;
      }

      if (useBusinessLocation && (!businessProfile?.address || !businessProfile.address.city)) {
        alert('Business location is incomplete. Please update your business profile or select a custom location.');
        setSubmitting(false);
        return;
      }

      // Get user data and validate businessId
      const userDoc = await getDoc(doc(firestore, 'users', user.uid));
      if (!userDoc.exists()) {
        alert('User profile not found. Please contact support.');
        setSubmitting(false);
        return;
      }
      
      const userData = userDoc.data();
      const businessId = userData.businessId;
      
      // Critical validation: Ensure businessId exists and is valid
      if (!businessId || typeof businessId !== 'string' || businessId.trim() === '') {
        console.error('❌ Critical: Missing or invalid businessId for user:', user.uid, { userData, businessId });
        alert('Error: Your account is not properly linked to a business. Please contact support.');
        setSubmitting(false);
        return;
      }
      
      // Verify the business actually exists
      const businessDoc = await getDoc(doc(firestore, 'businesses', businessId));
      if (!businessDoc.exists()) {
        console.error('❌ Critical: Business document not found for businessId:', businessId);
        alert('Error: Business profile not found. Please contact support.');
        setSubmitting(false);
        return;
      }
      
      console.log('✅ Business validation passed:', { userId: user.uid, businessId, businessName: businessDoc.data().name });

      // Determine job location
      let jobAddress;
      if (useBusinessLocation && businessProfile?.address) {
        // Use business location
        jobAddress = {
          ...businessProfile.address,
          coordinates: businessProfile.address.coordinates || {
            latitude: 0,
            longitude: 0
          }
        };
      } else if (customLocation) {
        // Use custom location
        jobAddress = customLocation;
      } else {
        // Fallback to basic business address without coordinates
        jobAddress = {
          city: businessProfile?.address?.city || '',
          state: businessProfile?.address?.state || '',
          street: businessProfile?.address?.street || '',
          zipcode: businessProfile?.address?.zipcode || '',
          coordinates: {
            latitude: 0,
            longitude: 0
          }
        };
      }

      // Build job data with required validation
      const jobData = {
        ...jobForm,
        businessId: businessId, // Explicitly set and validated
        address: jobAddress,
        applicationCount: selectedJob ? (selectedJob.applicationCount || 0) : 0,
        isActive: true,
        updatedAt: serverTimestamp(),
        createdBy: user.uid, // Track who created this job for future debugging
      };
      
      // Final validation: Double-check businessId is in the job data
      if (!jobData.businessId) {
        console.error('❌ Critical: businessId missing from job data before save:', jobData);
        alert('Error: Failed to set business information. Please try again.');
        setSubmitting(false);
        return;
      }

      if (selectedJob) {
        // Update existing job
        console.log('📝 Updating existing job:', selectedJob.id, 'with businessId:', jobData.businessId);
        await updateDoc(doc(firestore, 'jobs', selectedJob.id), jobData);
        // alert('Job updated successfully!'); // Disabled notification
      } else {
        // Create new job
        jobData.createdAt = serverTimestamp();
        console.log('📝 Creating new job with businessId:', jobData.businessId);
        await addDoc(collection(firestore, 'jobs'), jobData);
                  // alert('Job posted successfully!'); // Disabled notification
      }

      setShowCreateModal(false);
      setShowEditModal(false);
      resetJobForm();
      fetchJobs();
    } catch (error) {
      console.error('❌ Error submitting job:', error);
      alert('Error posting job. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [jobForm, useBusinessLocation, customLocation, businessProfile, selectedJob, resetJobForm, fetchJobs]);

  // Helper functions for managing arrays
  const addSkill = useCallback(() => {
    if (skillInput.trim() && !jobForm.requiredSkills.includes(skillInput.trim())) {
      setJobForm({
        ...jobForm,
        requiredSkills: [...jobForm.requiredSkills, skillInput.trim()]
      });
      setSkillInput('');
    }
  }, [skillInput, jobForm]);

  const removeSkill = useCallback((skillToRemove) => {
    setJobForm({
      ...jobForm,
      requiredSkills: jobForm.requiredSkills.filter(skill => skill !== skillToRemove)
    });
  }, [jobForm]);

  const addBenefit = useCallback(() => {
    if (benefitInput.trim() && !jobForm.extraCompensations.includes(benefitInput.trim())) {
      setJobForm({
        ...jobForm,
        extraCompensations: [...jobForm.extraCompensations, benefitInput.trim()]
      });
      setBenefitInput('');
    }
  }, [benefitInput, jobForm]);

  const removeBenefit = useCallback((benefitToRemove) => {
    setJobForm({
      ...jobForm,
      extraCompensations: jobForm.extraCompensations.filter(benefit => benefit !== benefitToRemove)
    });
  }, [jobForm]);

  const filteredJobs = jobs.filter(job => {
    if (filter === 'all') return true;
    if (filter === 'active') return job.isActive !== false;
    if (filter === 'inactive') return job.isActive === false;
    return true;
  });



  const getPostedDays = (createdAt) => {
    if (!createdAt) return 0;
    const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatJobLocation = (address) => {
    if (!address) return 'Location not specified';
    
    const parts = [];
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    
    return parts.length > 0 ? parts.join(', ') : 'Location not specified';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Job Posts</h1>
          <p className="text-gray-600">Manage your job listings and track applications to find the perfect candidates.</p>
          <button
            onClick={handleCreateJob}
            className="mt-4 bg-[#FF8C00] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#FF8C00]/90 transition-colors shadow-sm"
          >
            <svg className="inline mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Post New Job
          </button>
        </div>
      </div>

      {/* Job Filters */}
      <div className="mb-8 bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col space-y-6">
          {/* Header */}
          <div className="flex items-center">
            <div className="w-8 h-8 bg-[#FF8C00]/10 rounded-xl flex items-center justify-center mr-3">
              <svg className="w-4 h-4 text-[#FF8C00]" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-sm font-semibold text-gray-900">Filter Job Posts</span>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Status</label>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'All Jobs', icon: '📋', color: 'gray' },
                { key: 'active', label: 'Active', icon: '✅', color: 'green' },
                { key: 'inactive', label: 'Inactive', icon: '⏸️', color: 'red' }
              ].map((filterOption) => {
                const count = filterOption.key === 'all' ? jobs.length : 
                             filterOption.key === 'active' ? jobs.filter(j => j.isActive !== false).length :
                             jobs.filter(j => j.isActive === false).length;
                const isActive = filter === filterOption.key;
                
                return (
                  <button
                    key={filterOption.key}
                    onClick={() => setFilter(filterOption.key)}
                    className={`inline-flex items-center px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                      isActive
                        ? 'bg-[#FF8C00] text-white shadow-md transform scale-105'
                        : 'bg-slate-50 text-gray-700 hover:bg-gray-100 hover:scale-102'
                    }`}
                  >
                    <span className="mr-2">{filterOption.icon}</span>
                    {filterOption.label}
                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                      isActive 
                        ? 'bg-white/20 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Summary Stats */}
          <div className="pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>
                Showing {filteredJobs.length} of {jobs.length} job posts
              </span>
              <div className="flex items-center space-x-4">
                <span className="flex items-center">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                  {jobs.reduce((total, job) => total + (job.applicationCount || 0), 0)} total applications
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Jobs List */}
      {filteredJobs.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="mt-2 text-sm text-gray-500">
              {filter === 'all' ? 'No job posts yet' : `No ${filter} jobs`}
            </p>
            <p className="text-sm text-gray-500 mb-4">
              {filter === 'all' ? 'Create your first job listing to start hiring' : `Switch to a different filter to see other jobs`}
            </p>
            {filter === 'all' && (
              <button
                onClick={handleCreateJob}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
              >
                Post Your First Job
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
          <ul className="divide-y divide-gray-200">
            {filteredJobs.map((job) => (
              <li key={job.id} className="hover:bg-slate-50 transition-colors duration-150">
                <div className="px-6 py-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 pr-6">
                      {/* Header Section */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {job.title}
                            </h3>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              job.isActive !== false
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {job.isActive !== false ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div className="flex items-center space-x-4 mt-2">
                            <div className="flex items-center text-sm text-gray-500">
                              <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                              </svg>
                              {formatJobLocation(job.address)}
                            </div>
                            <div className="flex items-center text-sm text-gray-500">
                              <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                              </svg>
                              Posted {getPostedDays(job.createdAt)} days ago
                            </div>
                            <div className="flex items-center text-sm text-gray-500">
                              <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                              </svg>
                              {job.applicationCount || 0} applications
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Job Details Inline */}
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4 text-sm">
                        <div className="flex items-center">
                          <span className="text-gray-500 mr-2">Type:</span>
                          <span className="font-medium text-gray-900">{job.jobType}</span>
                        </div>
                        
                        {job.experienceLevel && (
                          <div className="flex items-center">
                            <span className="text-gray-500 mr-2">Experience:</span>
                            <span className="font-medium text-gray-900">{job.experienceLevel}</span>
                          </div>
                        )}
                        
                        {(job.hourlyRate || job.salary) && (
                          <div className="flex items-center">
                            <span className="text-gray-500 mr-2">Pay:</span>
                            <span className="font-semibold text-green-700">
                              {job.hourlyRate ? `$${job.hourlyRate}/hr` : job.salary}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Job Description */}
                      <div className="mb-4">
                        <p className="text-sm text-gray-700 leading-relaxed" style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {job.description}
                        </p>
                      </div>

                      {/* Skills */}
                      {job.requiredSkills && job.requiredSkills.length > 0 && (
                        <div>
                          <div className="flex flex-wrap gap-2">
                            {job.requiredSkills.slice(0, 6).map((skill, index) => (
                              <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {skill}
                              </span>
                            ))}
                            {job.requiredSkills.length > 6 && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                +{job.requiredSkills.length - 6} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action Panel */}
                    <div className="flex flex-col space-y-2 flex-shrink-0">
                      <button 
                        onClick={() => handleEditJob(job)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF8C00] transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                        Edit Job
                      </button>
                      
                      <button
                        onClick={() => navigate(`/business-dashboard/jobs/${job.id}`)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#FF8C00] hover:bg-[#FF8C00]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF8C00] shadow-sm transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                        </svg>
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Create Job Modal */}
      <JobModal 
        show={showCreateModal} 
        onClose={() => setShowCreateModal(false)} 
        isEdit={false}
        jobForm={jobForm}
        setJobForm={setJobForm}
        skillInput={skillInput}
        setSkillInput={setSkillInput}
        benefitInput={benefitInput}
        setBenefitInput={setBenefitInput}
        useBusinessLocation={useBusinessLocation}
        setUseBusinessLocation={setUseBusinessLocation}
        customLocation={customLocation}
        setCustomLocation={setCustomLocation}
        setShowLocationPicker={setShowLocationPicker}
        businessProfile={businessProfile}
        memoizedBusinessAddress={memoizedBusinessAddress}
        submitting={submitting}
        onSubmit={handleSubmitJob}
        onAddSkill={addSkill}
        onRemoveSkill={removeSkill}
        onAddBenefit={addBenefit}
        onRemoveBenefit={removeBenefit}
      />

      {/* Edit Job Modal */}
      <JobModal 
        show={showEditModal} 
        onClose={() => setShowEditModal(false)} 
        isEdit={true}
        jobForm={jobForm}
        setJobForm={setJobForm}
        skillInput={skillInput}
        setSkillInput={setSkillInput}
        benefitInput={benefitInput}
        setBenefitInput={setBenefitInput}
        useBusinessLocation={useBusinessLocation}
        setUseBusinessLocation={setUseBusinessLocation}
        customLocation={customLocation}
        setCustomLocation={setCustomLocation}
        setShowLocationPicker={setShowLocationPicker}
        businessProfile={businessProfile}
        memoizedBusinessAddress={memoizedBusinessAddress}
        submitting={submitting}
        onSubmit={handleSubmitJob}
        onAddSkill={addSkill}
        onRemoveSkill={removeSkill}
        onAddBenefit={addBenefit}
        onRemoveBenefit={removeBenefit}
      />
    </div>
  );
};

export default BusinessJobs; 